import React from 'react'
import { FieldValues, UseFormRegister } from 'react-hook-form'
import { FormField as FormFieldType, FormFieldType as FieldType } from '@/core/graphql/types'
import { Box, Typography } from '@mui/material'
import TextInput from '@/components/ui/inputs/TextInput'
import CheckboxInput from '@/components/ui/inputs/CheckboxInput'
import { RadioGroup } from '@headlessui/react'
import clsx from 'clsx'

type FormFieldValue = string | number | boolean | string[]

interface FormFieldProps {
  field: FormFieldType
  value?: FormFieldValue
  error?: string
  onChange?: (value: FormFieldValue) => void
  register?: UseFormRegister<FieldValues>
  disabled?: boolean
}

// Helper to safely treat value as a string array for Multiple fields
const toStringArray = (value: FormFieldValue | undefined): string[] => {
  if (Array.isArray(value)) return value
  if (typeof value === 'string' && value) {
    try {
      return JSON.parse(value)
    } catch {
      return []
    }
  }
  return []
}

const FormField: React.FC<FormFieldProps> = React.memo(({ field, value, error, onChange, register, disabled = false }) => {
  const handleChange = (val: FormFieldValue) => {
    if (onChange) {
      onChange(val)
    }
  }

  switch (field.type) {
    case FieldType.Text:
      return (
        <TextInput
          label={field.label}
          required={field.required}
          error={!!error}
          helperText={error}
          disabled={disabled}
          {...(register
            ? register(field.id)
            : { name: field.id, value: value ?? '', onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value) })}
        />
      )

    case FieldType.TextArea:
      return (
        <TextInput
          label={field.label}
          required={field.required}
          error={!!error}
          helperText={error}
          disabled={disabled}
          multiline
          rows={4}
          {...(register
            ? register(field.id)
            : { name: field.id, value: value ?? '', onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value) })}
        />
      )

    case FieldType.Multiple:
      const selectedValues = toStringArray(value)
      return (
        <div className="w-full">
          <div className="mb-2">
            <Typography variant="subtitle2" color={error ? 'error' : 'textSecondary'}>
              {field.label}
              {field.required && (
                <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
          </div>
          <div className="relative -space-y-px rounded-md bg-white">
            {field.options?.map((option, optionIdx) => {
              const isSelected = selectedValues.includes(option)
              return (
                <div
                  key={option}
                  className={clsx(
                    {
                      'rounded-tl-md rounded-tr-md': optionIdx === 0,
                      'rounded-bl-md rounded-br-md': optionIdx === (field.options?.length || 1) - 1,
                      'z-10 border-indigo-200 bg-indigo-50': isSelected,
                      'border-gray-200': !isSelected,
                    },
                    'relative flex cursor-pointer border border-solid p-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary'
                  )}
                  onClick={() => {
                    if (!disabled) {
                      const newValues = isSelected ? selectedValues.filter((v: string) => v !== option) : [...selectedValues, option]
                      handleChange(newValues)
                    }
                  }}
                >
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-sm">
                        <p className={clsx('font-medium', isSelected ? 'text-indigo-900' : 'text-gray-900')}>{option}</p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="shrink-0 text-indigo-600">
                        <CheckIcon className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </div>
      )

    case FieldType.Number:
      return (
        <TextInput
          label={field.label}
          required={field.required}
          error={!!error}
          helperText={error}
          disabled={disabled}
          type="number"
          {...(register
            ? register(field.id)
            : { name: field.id, value: value ?? '', onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value) })}
        />
      )

    case FieldType.Boolean:
      return (
        <Box>
          <CheckboxInput
            label={field.label}
            disabled={disabled}
            {...(register
              ? register(field.id)
              : { name: field.id, checked: Boolean(value), onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.checked) })}
          />
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </Box>
      )

    default:
      return null
  }
})

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx={12} cy={12} r={12} fill="currentColor" opacity="0.2" />
      <path d="M7 13l3 3 7-7" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

FormField.displayName = 'FormField'

export default FormField
